import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faSignOutAlt, faUser, faTasks, faShield, faHandHoldingHeart, faBell } from '@fortawesome/free-solid-svg-icons';
import { useReminder } from '../context/ReminderContext';

function ReminderSettingsPage() {
  const navigate = useNavigate();
  const { preferences, configs, updatePreferences, updateConfig } = useReminder();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [localPrefs, setLocalPrefs] = useState({
    enabled: true,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    max_reminders_per_hour: 4,
    default_snooze_duration: 900000,
    style: {
      background_color: '#000000',
      text_color: '#FFB6C1',
      font_size: 16,
      opacity: 0.95,
    },
  });

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (preferences) {
      setLocalPrefs({
        enabled: preferences.enabled,
        quiet_hours_start: preferences.quiet_hours_start,
        quiet_hours_end: preferences.quiet_hours_end,
        max_reminders_per_hour: preferences.max_reminders_per_hour,
        default_snooze_duration: preferences.default_snooze_duration,
        style: preferences.style || localPrefs.style,
      });
    }
  }, [preferences]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    setMessage('');
    try {
      await updatePreferences(localPrefs);
      setMessage('✓ Preferences saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('✗ Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleConfig = async (configId, enabled) => {
    try {
      await updateConfig(configId, { enabled });
    } catch (error) {
      console.error('Failed to update configuration');
    }
  };

  return (
    <div style={{
      minHeight: '100vh'
    }}>
      {/* Navigation Bar (same as Dashboard) */}
      <nav style={{
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #333',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img src="/bestie_logo.png" alt="Bestie" style={{ width: '1.5rem', height: '1.5rem' }} />
          <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#FFB6C1' }}>BestieConnect</span>
        </div>
        
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1rem' }}>
            <FontAwesomeIcon icon={faChartLine} /> Dashboard
          </button>
          <button onClick={() => navigate('/tasks')} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1rem' }}>
            <FontAwesomeIcon icon={faTasks} /> Tasks
          </button>
          <button onClick={() => navigate('/burnout-watch')} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1rem' }}>
            <FontAwesomeIcon icon={faShield} /> Burnout Watch
          </button>
          <button onClick={() => navigate('/recovery')} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1rem' }}>
            <FontAwesomeIcon icon={faHandHoldingHeart} /> Recovery
          </button>
          <button onClick={() => navigate('/settings/reminders')} style={{ background: 'none', border: 'none', color: '#FFB6C1', cursor: 'pointer', fontSize: '1rem' }}>
            <FontAwesomeIcon icon={faBell} /> Reminders
          </button>
          
          <div style={{ borderLeft: '1px solid #333', paddingLeft: '1rem', marginLeft: '1rem' }}>
            <span style={{ marginRight: '1rem', color: '#999' }}>
              <FontAwesomeIcon icon={faUser} /> {user.username || user.email}
            </span>
            <button onClick={handleLogout} style={{
              background: 'none',
              border: '1px solid #FFB6C1',
              color: '#FFB6C1',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}>
              <FontAwesomeIcon icon={faSignOutAlt} /> Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div style={{ padding: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#FFB6C1' }}>
            🔔 Reminder Settings
          </h1>
          <p style={{ color: '#999' }}>Customize your reminder preferences</p>
        </div>

        {/* Success/Error Message */}
        {message && (
          <div style={{
            padding: '1rem',
            backgroundColor: message.includes('✓') ? '#4ade8020' : '#ef444420',
            border: `1px solid ${message.includes('✓') ? '#4ade80' : '#ef4444'}`,
            borderRadius: '8px',
            color: message.includes('✓') ? '#4ade80' : '#ef4444',
            marginBottom: '1.5rem'
          }}>
            {message}
          </div>
        )}

        {/* General Settings */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ 
            color: '#FFB6C1', 
            marginTop: 0, 
            marginBottom: '1.5rem',
            fontSize: '1.2rem',
            borderBottom: '2px solid #FFB6C1',
            paddingBottom: '0.5rem'
          }}>
            General
          </h3>
          
          {/* Enable Reminders */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1.5rem',
            cursor: 'pointer',
            padding: '0.75rem',
            backgroundColor: '#0a0a0a',
            borderRadius: '8px'
          }}>
            <input
              type="checkbox"
              checked={localPrefs.enabled}
              onChange={(e) => setLocalPrefs({ ...localPrefs, enabled: e.target.checked })}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '1rem', color: '#fff' }}>Enable Reminders</span>
          </label>

          {/* Max Reminders Per Hour */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <label style={{ minWidth: '250px', color: '#999' }}>Max Reminders Per Hour:</label>
            <input
              type="number"
              min="1"
              max="10"
              value={localPrefs.max_reminders_per_hour}
              onChange={(e) => setLocalPrefs({
                ...localPrefs,
                max_reminders_per_hour: parseInt(e.target.value)
              })}
              style={{
                padding: '0.5rem',
                backgroundColor: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '6px',
                color: '#fff',
                width: '100px'
              }}
            />
          </div>

          {/* Default Snooze Duration */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ minWidth: '250px', color: '#999' }}>Default Snooze Duration (minutes):</label>
            <input
              type="number"
              min="5"
              max="60"
              value={localPrefs.default_snooze_duration / 60000}
              onChange={(e) => setLocalPrefs({
                ...localPrefs,
                default_snooze_duration: parseInt(e.target.value) * 60000
              })}
              style={{
                padding: '0.5rem',
                backgroundColor: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '6px',
                color: '#fff',
                width: '100px'
              }}
            />
          </div>
        </div>

        {/* Quiet Hours */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ 
            color: '#FFB6C1', 
            marginTop: 0, 
            marginBottom: '1.5rem',
            fontSize: '1.2rem',
            borderBottom: '2px solid #FFB6C1',
            paddingBottom: '0.5rem'
          }}>
            Quiet Hours
          </h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <label style={{ minWidth: '250px', color: '#999' }}>Start Time:</label>
            <input
              type="time"
              value={localPrefs.quiet_hours_start}
              onChange={(e) => setLocalPrefs({
                ...localPrefs,
                quiet_hours_start: e.target.value
              })}
              style={{
                padding: '0.5rem',
                backgroundColor: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '6px',
                color: '#fff',
                width: '150px'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ minWidth: '250px', color: '#999' }}>End Time:</label>
            <input
              type="time"
              value={localPrefs.quiet_hours_end}
              onChange={(e) => setLocalPrefs({
                ...localPrefs,
                quiet_hours_end: e.target.value
              })}
              style={{
                padding: '0.5rem',
                backgroundColor: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '6px',
                color: '#fff',
                width: '150px'
              }}
            />
          </div>
        </div>

        {/* Appearance */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ 
            color: '#FFB6C1', 
            marginTop: 0, 
            marginBottom: '1.5rem',
            fontSize: '1.2rem',
            borderBottom: '2px solid #FFB6C1',
            paddingBottom: '0.5rem'
          }}>
            Appearance
          </h3>
          
          {/* Background Color */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <label style={{ minWidth: '250px', color: '#999' }}>Background Color:</label>
            <input
              type="color"
              value={localPrefs.style.background_color}
              onChange={(e) => setLocalPrefs({
                ...localPrefs,
                style: { ...localPrefs.style, background_color: e.target.value }
              })}
              style={{
                width: '60px',
                height: '40px',
                border: '1px solid #333',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            />
            <span style={{ color: '#666', fontSize: '0.9rem' }}>{localPrefs.style.background_color}</span>
          </div>

          {/* Text Color */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <label style={{ minWidth: '250px', color: '#999' }}>Text Color:</label>
            <input
              type="color"
              value={localPrefs.style.text_color}
              onChange={(e) => setLocalPrefs({
                ...localPrefs,
                style: { ...localPrefs.style, text_color: e.target.value }
              })}
              style={{
                width: '60px',
                height: '40px',
                border: '1px solid #333',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            />
            <span style={{ color: '#666', fontSize: '0.9rem' }}>{localPrefs.style.text_color}</span>
          </div>

          {/* Font Size */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <label style={{ minWidth: '250px', color: '#999' }}>Font Size:</label>
            <input
              type="number"
              min="12"
              max="24"
              value={localPrefs.style.font_size}
              onChange={(e) => setLocalPrefs({
                ...localPrefs,
                style: { ...localPrefs.style, font_size: parseInt(e.target.value) }
              })}
              style={{
                padding: '0.5rem',
                backgroundColor: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '6px',
                color: '#fff',
                width: '100px'
              }}
            />
            <span style={{ color: '#666', fontSize: '0.9rem' }}>{localPrefs.style.font_size}px</span>
          </div>

          {/* Opacity */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ minWidth: '250px', color: '#999' }}>Opacity:</label>
            <input
              type="range"
              min="0.5"
              max="1"
              step="0.05"
              value={localPrefs.style.opacity}
              onChange={(e) => setLocalPrefs({
                ...localPrefs,
                style: { ...localPrefs.style, opacity: parseFloat(e.target.value) }
              })}
              style={{
                flex: 1,
                maxWidth: '300px'
              }}
            />
            <span style={{ color: '#FFB6C1', fontSize: '1rem', minWidth: '50px' }}>
              {(localPrefs.style.opacity * 100).toFixed(0)}%
            </span>
          </div>
        </div>

        {/* Reminder Types */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ 
            color: '#FFB6C1', 
            marginTop: 0, 
            marginBottom: '1.5rem',
            fontSize: '1.2rem',
            borderBottom: '2px solid #FFB6C1',
            paddingBottom: '0.5rem'
          }}>
            Reminder Types
          </h3>
          
          <div style={{ display: 'grid', gap: '1rem' }}>
            {configs && configs.length > 0 ? (
              configs.map((config) => (
                <label 
                  key={config.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1rem',
                    backgroundColor: '#0a0a0a',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: `1px solid ${config.enabled ? '#FFB6C1' : '#333'}`,
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={(e) => handleToggleConfig(config.id, e.target.checked)}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span style={{
                    fontSize: '1rem',
                    fontWeight: '500',
                    color: config.enabled ? '#fff' : '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {config.reminder_type.replace(/_/g, ' ')}
                  </span>
                </label>
              ))
            ) : (
              <div style={{ color: '#999', textAlign: 'center', padding: '1rem' }}>
                Loading reminder types...
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSavePreferences}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#999' : '#FFB6C1',
            color: saving ? '#666' : '#000',
            border: 'none',
            padding: '1rem 2rem',
            fontSize: '1.1rem',
            fontWeight: '600',
            borderRadius: '8px',
            cursor: saving ? 'not-allowed' : 'pointer',
            width: '100%',
            opacity: saving ? 0.6 : 1,
            transition: 'all 0.2s'
          }}
        >
          {saving ? 'Saving...' : '💾 Save Preferences'}
        </button>

        {/* Info Box */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          color: '#999',
          fontSize: '0.9rem'
        }}>
          <strong style={{ color: '#FFB6C1' }}>💡 Tip:</strong> Reminders appear as a calm overlay on your screen. 
          Customize the appearance above to match your preference. Quiet hours prevent reminders during sleep or focus times.
        </div>
      </div>
    </div>
  );
}

export default ReminderSettingsPage;
